import React, { useRef, useState, useEffect, useCallback } from "react";

const DragScrollContainer = ({
  children,
  className = "",
  disabled = false,
  onScrollStart,
  onScrollEnd,
}) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, scrollLeft: 0 });
  const [hasScrolled, setHasScrolled] = useState(false);

  const handleStart = (clientX) => {
    if (disabled || !containerRef.current) return;

    setIsDragging(true);
    setHasScrolled(false);
    setStartPosition({
      x: clientX,
      scrollLeft: containerRef.current.scrollLeft,
    });

    // ドラッグ開始時にスクロール動作を無効化
    containerRef.current.style.scrollBehavior = "auto";

    onScrollStart?.();

    // ドラッグ中はテキスト選択を無効化
    document.body.style.userSelect = "none";
  };

  const handleMove = (clientX) => {
    if (!isDragging || !containerRef.current) return;

    const deltaX = startPosition.x - clientX;
    const newScrollLeft = startPosition.scrollLeft + deltaX;

    // スクロール位置を直接設定（慣性なし）
    containerRef.current.style.scrollBehavior = "auto";
    containerRef.current.scrollLeft = newScrollLeft;

    // 移動距離が5px以上の場合、スクロールしたとみなす
    if (Math.abs(deltaX) > 5) {
      setHasScrolled(true);
    }
  };

  const handleEnd = () => {
    if (!isDragging) return;

    document.body.style.userSelect = "";

    // スクロール動作をスムーズに戻す
    if (containerRef.current) {
      containerRef.current.style.scrollBehavior = "smooth";
    }

    onScrollEnd?.();

    // 参考コードと同様の50ms遅延でドラッグ状態をリセット
    setTimeout(() => {
      setIsDragging(false);
      if (hasScrolled) {
        setHasScrolled(false);
      }
    }, 50);
  };

  // マウスイベント
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleStart(e.clientX);
  };

  // タッチイベント
  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    handleMove(e.touches[0].clientX);
  };

  // グローバルイベントリスナー
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      e.preventDefault();
      handleMove(e.clientX);
    };

    const handleMouseUp = (e) => {
      e.preventDefault();
      handleEnd();
    };

    // より確実にイベントをキャッチ
    document.addEventListener("mousemove", handleMouseMove, {
      passive: false,
      capture: true,
    });
    document.addEventListener("mouseup", handleMouseUp, {
      passive: false,
      capture: true,
    });

    return () => {
      document.removeEventListener("mousemove", handleMouseMove, {
        capture: true,
      });
      document.removeEventListener("mouseup", handleMouseUp, { capture: true });
    };
  }, [isDragging, startPosition.x, startPosition.scrollLeft]);

  // クリックイベントを制御する関数
  const handleClick = useCallback(
    (e) => {
      // スクロールした場合のみクリックを無効化
      if (hasScrolled) {
        console.log("Click prevented due to scrolling");
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      // 通常のクリックは通す
      console.log("Click allowed");
    },
    [hasScrolled]
  );

  // 子要素にクリック制御情報を渡すためのプロバイダー
  const contextValue = {
    isClickDisabled: hasScrolled,
    isDragging,
  };

  return (
    <div
      ref={containerRef}
      className={`drag-scroll-container ${className} ${
        isDragging ? "dragging" : ""
      }`}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleEnd}
      onClick={handleClick}
      style={{
        cursor: isDragging ? "grabbing" : "grab",
        overflowX: "auto",
        overflowY: "hidden",
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
      }}
      data-click-disabled={hasScrolled}
    >
      {children}
    </div>
  );
};

export default DragScrollContainer;
